import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import { Card, Row, Alert } from "react-bootstrap";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import {
  GROUPBY_ID_JOBTITLE_GET_DATA,
  GROUPBYTYPE_ADMIN_GET_DATA,
} from "../../../group_jobtitles/redux/Constants";
import FormTextField from "../../../../shared/components/FormTextField";
import FormikSelectField from "../../../../shared/components/FormikSelectField";
import FormTextAreaField from "../../../../shared/components/FormTextAreaField";

import { loadCategoryDropdownRequest, loadStatusDropdownRequest } from "../redux/Actions";
import FormikCheckboxField from "../../../../shared/components/FormikCheckboxField";
import { loadCompanyDataRequest } from "../../../company/redux/Actions";

// Constant dropdown options
const EXECUTION_MODE_OPTIONS = [
  { value: "scheduled", label: "Scheduled (Cron Job)" },
  { value: "ticket_creation", label: "On Ticket Create" },
  { value: "ticket_update", label: "On Ticket Update" },
];

const TICKET_TYPE_OPTIONS = [
  { value: "Ticket", label: "Ticket" },
  { value: "Workflow", label: "Workflow" },
];

// Watches Admin Group value and dispatches Redux call to load users
function GroupWatcher({ dispatch }) {
  const { values } = useFormikContext();

  useEffect(() => {
    if (values.selectedGroup) {
      dispatch({
        type: GROUPBY_ID_JOBTITLE_GET_DATA.GROUPBY_ID_JOBTITLE_GET_DATA_LOAD,
        id: values.selectedGroup,
      });
    }
  }, [values.selectedGroup, dispatch]);

  return null;
}

// Watches Company and re-fetches categories (all if no company selected)
function CompanyWatcher({ dispatch }) {
  const { values } = useFormikContext();

  useEffect(() => {
    if (values.company) {
      dispatch(loadCategoryDropdownRequest({ companyId: values.company }));
    } else {
      dispatch(loadCategoryDropdownRequest());
    }
  }, [values.company, dispatch]);

  return null;
}

// Filters the fetched categories by ticket type + admin group
function CategoryWatcher({ loadCategories, categoryList }) {
  const { values } = useFormikContext();

  useEffect(() => {
    loadCategories(values.selectedGroup, values.ticketType);
  }, [values.selectedGroup, values.ticketType, categoryList]);

  return null;
}

const RuleConfiguration = forwardRef(function RuleConfiguration(
  { isEdit, initialData, executionMode, setExecutionMode },
  ref
) {
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const { group, adminGroup, loading, error } = useSelector(
    (state) => state.groupJobtitle,
    shallowEqual
  );
  const { categoryList, statusList, loadingStatus } =
    useSelector((state) => state.ticketingDropdowns);

  const { login } = useSelector((state) => state.login, shallowEqual);
  const userRole = "admin"; //login?.role;

  const { companies } = useSelector((state) => state.company, shallowEqual);

  // Local state for dropdown data
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    if (!companies?.length) dispatch(loadCompanyDataRequest());
  }, []);

  useImperativeHandle(ref, () => ({
    async validateAndGetData() {
      if (!formikRef.current) {
        return { isValid: false };
      }

      const errors = await formikRef.current.validateForm();
      const isValid = Object.keys(errors).length === 0;

      // company is display-only → never sent to the API
      const { company, ...data } = formikRef.current.values;

      if (!isValid) {
        console.warn("Validation errors found:", errors);

        formikRef.current.setTouched(
          Object.keys(formikRef.current.initialValues).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {}
          )
        );

        return { isValid: false, data };
      }

      return { isValid: true, data };
    },
  }));

  // Load admin groups based on user role
  useEffect(() => {
    dispatch({
      type: GROUPBYTYPE_ADMIN_GET_DATA.GROUPBYTYPE_ADMIN_GET_DATA_LOAD,
      role: userRole,
    });
  }, [dispatch, userRole]);

  useEffect(() => {
    if (!statusList.length && !loadingStatus) {
      dispatch(loadStatusDropdownRequest());
    }
  }, []);

  const loadCategories = (selectedGroup, selectedTicketType) => {
    // Fetching is handled by CompanyWatcher; here we only filter
    if (!categoryList || categoryList.length === 0) {
      setCategoryOptions([]);
      return;
    }

    // Step 1: ALWAYS filter by ticket type first
    const ticketFiltered = categoryList.filter(
      (cat) => String(cat.ticket_type) === String(selectedTicketType)
    );

    // Step 2: If no admin group selected → show only ticket-type filtered list
    if (!selectedGroup) {
      setCategoryOptions(ticketFiltered);
      return;
    }

    // Step 3: Apply Group-based filtering
    const groupId = String(selectedGroup);

    const fullyFiltered = ticketFiltered.filter(
      (cat) =>
        String(cat.group_id ?? "") === groupId ||
        String(cat.default_admin_group_id ?? "") === groupId ||
        String(cat.admin_group_id ?? "") === groupId ||
        String(cat.default_group_id ?? "") === groupId
    );

    setCategoryOptions(fullyFiltered);
  };

  // Yup validation rules
  const validationSchema = Yup.object({
    ruleName: Yup.string().trim().required("Rule name is required."),
    description: Yup.string().trim().required("Description is required."),
    selectedStatus: Yup.string().required("Please select a status."),
  });

  // Edit mode: preset Company from the saved category (runs once)
function EditCompanyPreset({ isEdit, categoryList }) {
  const { values, setFieldValue } = useFormikContext();
  const done = useRef(false);

  useEffect(() => {
    if (!isEdit || done.current || values.company || !values.category) return;
    if (!categoryList?.length) return;

    const cat = categoryList.find(
      (c) => String(c.value) === String(values.category)
    );
    const companyId = Array.isArray(cat?.company_id)
      ? cat.company_id[0]
      : cat?.company_id;

    if (companyId) {
      done.current = true;
      setFieldValue("company", String(companyId));
    }
  }, [isEdit, categoryList, values.category]);

  return null;
}

  return (
    <Card className="p-4 shadow-sm bg-white">
      <Card.Header>Rule Configuration</Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" className="py-2">
            Failed to load admin groups. Please try again later.
          </Alert>
        )}

        <Formik
          initialValues={{
            is_active: isEdit ? initialData?.is_active : true,
            ruleName: isEdit ? initialData?.rule_name : "",
            description: isEdit ? initialData?.description : "",
            company: "",
            category: isEdit
              ? initialData?.category_config_id === null
                ? ""
                : initialData?.category_config_id
              : "",
            ticketType: isEdit ? initialData?.ticket_type : "Ticket",
            executionMode: isEdit ? initialData?.execution_mode : "scheduled",
            selectedGroup: isEdit ? initialData?.admin_group_id : "",
            selectedUser: isEdit ? initialData?.assigned_to_id : "",
            selectedStatus: isEdit ? initialData?.status ?? "" : "",
          }}
          enableReinitialize={true}
          validationSchema={validationSchema}
          innerRef={formikRef}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <GroupWatcher dispatch={dispatch} />
              <CompanyWatcher dispatch={dispatch} />
              <EditCompanyPreset isEdit={isEdit} categoryList={categoryList} />
              <CategoryWatcher
                loadCategories={loadCategories}
                categoryList={categoryList}
              />

              {/* Is Active Checkbox */}
              <div style={{ marginBottom: "10px" }}>
                <FormikCheckboxField
                  name="is_active"
                  label="Active"
                  value={values.is_active}
                  setFieldValue={setFieldValue}
                />
              </div>

              {/* Rule name and execution mode */}
              <Row className="g-3">
                <FormTextField
                  name="ruleName"
                  label="Rule Name"
                  required
                  placeholder="Enter rule name"
                />

                <FormikSelectField
                  name="executionMode"
                  label="Execution Mode"
                  required
                  value={values.executionMode}
                  onChange={(name, value) => {
                    setFieldValue(name, value);
                    setExecutionMode(value); // send to parent
                  }}
                >
                  {EXECUTION_MODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </FormikSelectField>
              </Row>

              <div className="form-textarea-field mt-3">
                <FormTextAreaField
                  name="description"
                  label="Description"
                  required
                  placeholder="Enter rule description"
                />
              </div>

              {/* Admin Group and Assigned To */}
              <Row className="mt-1 g-3">
                <FormikSelectField
                  name="selectedGroup"
                  label="Admin Group"
                  value={values.selectedGroup}
                  onChange={setFieldValue}
                  placeholder="-- Select Admin Group --"
                  disabled={loading}
                >
                  <option value="">-- Select Admin Group --</option>
                  {Array.isArray(adminGroup) &&
                    adminGroup.map((g) => (
                      <option key={g.group_id ?? g.id} value={g.group_id ?? g.id}>
                        {g.group_name}
                      </option>
                    ))}
                </FormikSelectField>

                <FormikSelectField
                  name="selectedUser"
                  label="Assigned To"
                  value={values.selectedUser}
                  onChange={setFieldValue}
                  placeholder={!values.selectedGroup ? "Select a group first" : "Select User"}
                  disabled={!values.selectedGroup || loading}
                >
                  <option value="">
                    {!values.selectedGroup
                      ? "Select a group first"
                      : Array.isArray(group) && group.length
                      ? "-- Select User --"
                      : "No users available"}
                  </option>
                  {(Array.isArray(group) ? group : []).map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.emp_name ?? user.name}
                    </option>
                  ))}
                </FormikSelectField>
              </Row>

              {/* Status and Ticket Type */}
              <Row className="mt-2 g-3">
                <FormikSelectField
                  name="selectedStatus"
                  label="Status"
                  required
                  value={values.selectedStatus}
                  onChange={setFieldValue}
                >
                  <option value="">-- Select Status --</option>
                  {statusList.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </FormikSelectField>

                <FormikSelectField
                  name="ticketType"
                  label="Ticket Type"
                  required
                  value={values.ticketType}
                  onChange={setFieldValue}
                >
                  {TICKET_TYPE_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </FormikSelectField>
              </Row>

              {/* Company and Category */}
              <Row className="mt-3 g-3">
                <FormikSelectField
                  name="company"
                  label="Company"
                  value={values.company}
                  onChange={(name, value) => {
                    setFieldValue(name, value);
                    setFieldValue("category", ""); // reset category on company change
                  }}
                >
                  <option value="">All Companies</option>
                  {(companies || []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.company_name}
                    </option>
                  ))}
                </FormikSelectField>

                <FormikSelectField
                  name="category"
                  label="Category"
                  value={values.category}
                  onChange={setFieldValue}
                >
                  <option value="">All Categories</option>
                  {categoryOptions.map((item) => (
                    <option key={item.id || item.value} value={item.id || item.value}>
                      {item.category_name || item.label || item.name}
                    </option>
                  ))}
                </FormikSelectField>
              </Row>
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  );
});

export default RuleConfiguration;